import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FailureFilters, DEFAULT_FAILURE_FILTERS } from '@/types/filters';
import { vblForecastApi } from '@/services/api/vblForecastApi';
import type {
  VblForecastProject,
  VblForecastResult,
  VblForecastConfiguration
} from '@/types/vblForecast';
import { DEFAULT_VBL_FORECAST_CONFIG } from '@/types/vblForecast';
import type { TurbineToForecast } from '@/types/vbl';
import type { TurbineWorkspaceMapping } from '@/types/turbineWorkspaceMapping';
import {
  calculateMonteCarloVBL,
  hybridSampleConditional,
  type MonteCarloVBLOptions
} from '@/utils/monteCarloVBL';
import { VblAnalysisResult } from '@/types/vbl';
import { vblApi } from '@/services/api/vblApi';

/**
 * Parametric bootstrap calculation for VBL forecasting
 * Uses Weibull parameters with uncertainty to predict failure times
 */
class VblForecastCalculator {
  /**
   * Sample from normal distribution using Box-Muller transform
   */
  private sampleFromNormal(mean: number, stdDev: number): number {
    const u1 = Math.random();
    const u2 = Math.random();
    const z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    return mean + z0 * stdDev;
  }

  /**
   * Calculate Weibull CDF (cumulative distribution function)
   */
  private weibullCDF(t: number, shape: number, scale: number): number {
    if (t <= 0) return 0;
    return 1 - Math.exp(-Math.pow(t / scale, shape));
  }

  /**
   * Calculate Weibull reliability function
   */
  private weibullReliability(t: number, shape: number, scale: number): number {
    return 1 - this.weibullCDF(t, shape, scale);
  }

  /**
   * Calculate conditional failure time using enhanced Monte Carlo method
   * Now supports both rejection sampling and inverse transform approaches
   */
  private calculateConditionalFailureTime(
    currentTime: number,
    shape: number,
    scale: number,
    _samplingMethod: 'rejection' | 'inverse-transform' | 'hybrid' = 'hybrid'
  ): number {
    // Use the new hybrid sampling approach for best accuracy and efficiency
    const result = hybridSampleConditional(shape, scale, currentTime, 100);
    return result.value;
  }

  /**
   * VBL forecast calculation using Johnson's method Monte Carlo VBL when available
   */
  async calculateTurbineForecast(
    turbine: TurbineToForecast,
    vblParameters: VblForecastProject['vblParameters'],
    currentDegradationTime: number,
    configuration: VblForecastConfiguration,
    mapping?: TurbineWorkspaceMapping
  ): Promise<VblForecastResult | null> {
    // Try to get VBL analysis results if we have a mapping
    let vblAnalysisResult: VblAnalysisResult | null = null;

    // Use Monte Carlo VBL for enhanced results (always enabled)
    const useMonteCarloVBL = true;

    if (mapping) {
      try {
        // Create filters based on the workspace mapping
        const filters: FailureFilters = {
          // Use the major component from the mapping to filter VBL data
          componentsMajor: mapping.major_component_name ? [mapping.major_component_name] : undefined,
          // If mapping specifies a specific sub-component, filter by that too
          componentsMinor: mapping.component_name ? [mapping.component_name] : undefined,
          sites: turbine.site_name ? [turbine.site_name] : undefined,
        };

        // Fetch VBL analysis for this workspace's component/site combination
        const vblData = await vblApi.getVblForWeibullAnalysis(filters);

        if (vblData.vbls && vblData.vbls.length >= 2) {
          // Use the existing VBL analysis logic to calculate Johnson's method parameters
          // This will give us the same analysis that would be shown in the VBL analysis page
          const { calculateJohnsonVbl } = await import('@/hooks/useVblAnalysis');
          vblAnalysisResult = calculateJohnsonVbl(vblData.vbls, configuration.confidenceLevel);
        }
      } catch (error) {
        console.warn('Failed to fetch VBL analysis for enhanced forecasting:', error);
        // Continue with fallback method
      }
    }

    // PRIMARY METHOD: Use Monte Carlo VBL for enhanced probabilistic results
    if (vblAnalysisResult && useMonteCarloVBL) {
      const monteCarloOptions: MonteCarloVBLOptions = {
        numSamples: configuration.bootstrapSamples,
        confidenceLevel: configuration.confidenceLevel,
        samplingMethod: configuration.samplingMethod || 'hybrid',
        rejectionSampleLimit: configuration.rejectionSampleLimit || 1000,
        useLogNormalScale: configuration.useLogNormalScale ?? true,
        correlationThreshold: 0.1,
        // Survival adaptation settings
        useSurvivalAdaptation: configuration.useSurvivalAdaptation ?? true,
        survivalAdaptationStrength: configuration.survivalAdaptationStrength ?? 0.3,
        earlyStageThreshold: configuration.earlyStageThreshold ?? 0.4
      };

      const vblResult = calculateMonteCarloVBL(vblAnalysisResult, currentDegradationTime, monteCarloOptions);

      if (vblResult) {
        // Convert VBL result to VblForecastResult format
        const startDate = new Date(configuration.startDate);
        const predictedFailureDate = new Date(startDate.getTime() + vblResult.median * 24 * 60 * 60 * 1000);
        const lowerDate = new Date(startDate.getTime() + vblResult.confidenceInterval.lower * 24 * 60 * 60 * 1000);
        const upperDate = new Date(startDate.getTime() + vblResult.confidenceInterval.upper * 24 * 60 * 60 * 1000);

        // Convert probability data format
        const probabilityOfFailureBy = vblResult.probabilityOfFailureBy.map(p => ({
          date: new Date(startDate.getTime() + p.days * 24 * 60 * 60 * 1000).toISOString(),
          days: p.days,
          probability: p.probability,
          confidenceInterval: configuration.includeConfidenceIntervals ? {
            lower: Math.max(0, p.probability - 0.05), // Simplified CI
            upper: Math.min(1, p.probability + 0.05)
          } : undefined
        }));

        return {
          turbine,
          currentDegradationTime,
          hasMapping: !!mapping,
          workspaceMapping: mapping,
          predictedFailureDate: predictedFailureDate.toISOString(),
          confidenceInterval: {
            lower: lowerDate.toISOString(),
            upper: upperDate.toISOString()
          },
          riskLevel: this.calculateRiskLevel(vblResult.median),
          timeToFailure: {
            days: vblResult.median,
            confidenceInterval: {
              lower: vblResult.confidenceInterval.lower,
              upper: vblResult.confidenceInterval.upper
            }
          },
          probabilityOfFailureBy,
          simulationMetadata: {
            bootstrapSamples: vblResult.simulationMetadata.samplesGenerated,
            convergenceAchieved: vblResult.simulationMetadata.convergenceAchieved,
            calculatedAt: new Date().toISOString(),
            samplingMethod: vblResult.simulationMetadata.samplingMethod,
            rejectionEfficiency: vblResult.simulationMetadata.rejectionEfficiency
          }
        };
      }
    }

    // No forecast without proper VBL analysis
    return null;
  }

  /**
   * Parametric bootstrap fallback method (original approach)
   */
  private calculateParametricBootstrap(
    turbine: TurbineToForecast,
    vblParameters: VblForecastProject['vblParameters'],
    currentDegradationTime: number,
    configuration: VblForecastConfiguration,
    mapping?: TurbineWorkspaceMapping
  ): VblForecastResult {
    const bootstrapSamples = configuration.bootstrapSamples;
    const failureTimes: number[] = [];

    // Perform bootstrap sampling
    for (let i = 0; i < bootstrapSamples; i++) {
      // Sample parameters from their distributions
      let shape = vblParameters.shape;
      let scale = vblParameters.scale;

      if (configuration.useBootstrapSimulation && vblParameters.standardErrors) {
        // Sample from parameter uncertainty
        shape = this.sampleFromNormal(
          vblParameters.shape,
          vblParameters.standardErrors.shape
        );
        scale = this.sampleFromNormal(
          vblParameters.scale,
          vblParameters.standardErrors.scale
        );

        // Ensure positive parameters
        shape = Math.max(0.1, shape);
        scale = Math.max(1, scale);
      }

      // Calculate conditional failure time
      const failureTime = this.calculateConditionalFailureTime(
        currentDegradationTime,
        shape,
        scale
      );

      failureTimes.push(failureTime);
    }

    // Calculate statistics
    failureTimes.sort((a, b) => a - b);
    const median = failureTimes[Math.floor(failureTimes.length * 0.5)];
    const lowerBound = failureTimes[Math.floor(failureTimes.length * (1 - configuration.confidenceLevel) / 2)];
    const upperBound = failureTimes[Math.floor(failureTimes.length * (configuration.confidenceLevel + (1 - configuration.confidenceLevel) / 2))];

    // Calculate days to failure
    const daysToFailure = median - currentDegradationTime;
    const daysToFailureLower = lowerBound - currentDegradationTime;
    const daysToFailureUpper = upperBound - currentDegradationTime;

    // Calculate failure dates
    const startDate = new Date(configuration.startDate);
    const predictedFailureDate = new Date(startDate.getTime() + daysToFailure * 24 * 60 * 60 * 1000);
    const lowerDate = new Date(startDate.getTime() + daysToFailureLower * 24 * 60 * 60 * 1000);
    const upperDate = new Date(startDate.getTime() + daysToFailureUpper * 24 * 60 * 60 * 1000);

    // Calculate probability of failure over time horizons
    const probabilityOfFailureBy: VblForecastResult['probabilityOfFailureBy'] = [];
    const timeHorizons = [30, 60, 90, 180, 365]; // Days

    for (const days of timeHorizons) {
      const targetTime = currentDegradationTime + days;
      const failuresBeforeTarget = failureTimes.filter(t => t <= targetTime).length;
      const probability = failuresBeforeTarget / failureTimes.length;

      const horizonDate = new Date(startDate.getTime() + days * 24 * 60 * 60 * 1000);

      probabilityOfFailureBy.push({
        date: horizonDate.toISOString(),
        days,
        probability,
        confidenceInterval: configuration.includeConfidenceIntervals ? {
          lower: Math.max(0, probability - 0.1), // Simplified CI calculation
          upper: Math.min(1, probability + 0.1)
        } : undefined
      });
    }

    return {
      turbine,
      currentDegradationTime,
      hasMapping: !!mapping,
      workspaceMapping: mapping,
      predictedFailureDate: predictedFailureDate.toISOString(),
      confidenceInterval: {
        lower: lowerDate.toISOString(),
        upper: upperDate.toISOString()
      },
      riskLevel: this.calculateRiskLevel(daysToFailure),
      timeToFailure: {
        days: daysToFailure,
        confidenceInterval: {
          lower: daysToFailureLower,
          upper: daysToFailureUpper
        }
      },
      probabilityOfFailureBy,
      simulationMetadata: {
        bootstrapSamples,
        convergenceAchieved: true, // Simplified for now
        calculatedAt: new Date().toISOString()
      }
    };
  }

  /**
   * Calculate risk level based on time to failure
   */
  private calculateRiskLevel(daysToFailure: number): 'Low' | 'Medium' | 'High' {
    if (daysToFailure <= 30) return 'High';      // Less than 1 month
    if (daysToFailure <= 90) return 'Medium';    // 1-3 months
    return 'Low';                                // More than 3 months
  }
}

/**
 * Find the best component mapping for a turbine based on component hierarchy
 * Priority: Major + Sub-component > Specific component only > Major component only > None (undefined)
 */
function findBestComponentMapping(
  turbine: TurbineToForecast,
  mappings: TurbineWorkspaceMapping[]
): TurbineWorkspaceMapping | undefined {
  // Get all mappings for this turbine
  const turbineMappings = mappings.filter(m => m.turbine_id === turbine.turbine_id);

  if (turbineMappings.length === 0) {
    return undefined;
  }

  // Helper function to safely check and compare values
  const isValidValue = (value: unknown): boolean => {
    return value !== null && value !== undefined && String(value).trim() !== '';
  };

  // Priority 1: Major component + Sub-component match (most specific)
  const majorAndSubMatch = turbineMappings.find(m => {
    const mappingMajorValid = isValidValue(m.major_component_id);
    const mappingComponentValid = isValidValue(m.component_id);
    const turbineMajorValid = isValidValue(turbine.major_component_id);
    const turbineComponentValid = isValidValue(turbine.component_id);

    return mappingMajorValid && mappingComponentValid &&
      turbineMajorValid && turbineComponentValid &&
      m.major_component_id === turbine.major_component_id &&
      m.component_id === turbine.component_id;
  });

  if (majorAndSubMatch) {
    return majorAndSubMatch;
  }

  // Priority 2: Exact specific component match (without requiring major component match)
  const exactComponentMatch = turbineMappings.find(m => {
    const mappingComponentValid = isValidValue(m.component_id);
    const turbineComponentValid = isValidValue(turbine.component_id);
    return mappingComponentValid && turbineComponentValid && m.component_id === turbine.component_id;
  });

  if (exactComponentMatch) {
    return exactComponentMatch;
  }

  // Priority 3: Major component only match (fallback)
  const majorComponentMatch = turbineMappings.find(m => {
    const mappingMajorValid = isValidValue(m.major_component_id);
    const mappingComponentInvalid = !isValidValue(m.component_id); // Only major, no sub-component
    const turbineMajorValid = isValidValue(turbine.major_component_id);
    return mappingMajorValid && mappingComponentInvalid && turbineMajorValid &&
      m.major_component_id === turbine.major_component_id;
  });

  if (majorComponentMatch) {
    return majorComponentMatch;
  }

  // No valid component mapping found
  return undefined;
}

/**
 * Hook for VBL forecast calculation with parametric bootstrapping
 */
export function useVblForecastCalculation({
  filters = DEFAULT_FAILURE_FILTERS,
  configuration = DEFAULT_VBL_FORECAST_CONFIG,
  autoCalculate = false
}: {
  filters?: FailureFilters;
  configuration?: VblForecastConfiguration;
  autoCalculate?: boolean;
} = {}) {
  const [forecastResults, setForecastResults] = useState<VblForecastResult[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState(0);
  const [progressText, setProgressText] = useState('');

  // Initialize calculator
  const calculator = useMemo(() => new VblForecastCalculator(), []);

  // Query for VBL forecast data
  const {
    data: vblForecastData,
    isLoading: isLoadingData,
    error: dataError,
    refetch: refetchData
  } = useQuery({
    queryKey: ['vbl-forecast-data', filters],
    queryFn: () => vblForecastApi.getVblForecastData(filters),
    staleTime: 60000, // 1 minute
    cacheTime: 300000, // 5 minutes
  });

  // Calculate forecast
  const calculateForecast = useCallback(async (
    selectedTurbines?: TurbineToForecast[],
    selectedWorkspaceId?: string
  ) => {
    if (!vblForecastData || vblForecastData.turbinesToForecast.length === 0) {
      console.warn('No VBL forecast data available');
      return;
    }

    setIsCalculating(true);
    setCalculationProgress(0);
    setProgressText('Initializing forecast calculation...');

    try {
      const turbinesToProcess = selectedTurbines || vblForecastData.turbinesToForecast;
      const results: VblForecastResult[] = [];

      // Group turbines by workspace mapping using component-aware matching
      const turbinesByWorkspace = new Map<string, { turbine: TurbineToForecast; mapping: TurbineWorkspaceMapping }[]>();

      for (const turbine of turbinesToProcess) {
        // Find the best component mapping for this turbine
        const mapping = findBestComponentMapping(turbine, vblForecastData.mappings);

        // Only process turbines that have valid mappings and workspace parameters
        if (mapping &&
          vblForecastData.workspaceParameters[mapping.workspace_id] &&
          (!selectedWorkspaceId || mapping.workspace_id === selectedWorkspaceId)) {

          if (!turbinesByWorkspace.has(mapping.workspace_id)) {
            turbinesByWorkspace.set(mapping.workspace_id, []);
          }
          turbinesByWorkspace.get(mapping.workspace_id)!.push({ turbine, mapping });
        }
      }

      // Calculate total turbines that will actually be processed (only mapped ones)
      const totalTurbinesToProcess = Array.from(turbinesByWorkspace.values()).reduce((sum, arr) => sum + arr.length, 0);
      let processedTurbines = 0;


      // Process only mapped turbines with valid component matching
      for (const [workspaceId, turbineDataArray] of Array.from(turbinesByWorkspace.entries())) {
        const parameters = vblForecastData.workspaceParameters[workspaceId];

        setProgressText(`Processing ${turbineDataArray.length} turbines...`);

        for (const { turbine, mapping } of turbineDataArray) {
          const currentDegradationTime = vblForecastApi.calculateCurrentDegradationTime(turbine);

          const result = await calculator.calculateTurbineForecast(
            turbine,
            parameters,
            currentDegradationTime,
            configuration,
            mapping
          );

          // Only add result if forecast was successful
          if (result !== null) {
            results.push(result);
          }

          processedTurbines++;
          setCalculationProgress(totalTurbinesToProcess > 0 ? (processedTurbines / totalTurbinesToProcess) * 100 : 100);
        }
      }

      setForecastResults(results);
      setProgressText('Forecast calculation completed');

    } catch (error) {
      console.error('VBL forecast calculation failed:', error);
      setProgressText('Forecast calculation failed');
    } finally {
      setIsCalculating(false);
      setCalculationProgress(100);
    }
  }, [vblForecastData, configuration, calculator]);

  // Clear results
  const clearResults = useCallback(() => {
    setForecastResults([]);
    setCalculationProgress(0);
    setProgressText('');
  }, []);

  // Validation
  const canCalculate = useMemo(() => {
    return !isLoadingData &&
      !isCalculating &&
      vblForecastData &&
      vblForecastData.turbinesToForecast.length > 0 &&
      Object.keys(vblForecastData.workspaceParameters).length > 0;
  }, [isLoadingData, isCalculating, vblForecastData]);

  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    if (!vblForecastData) {
      errors.push('No forecast data available');
    } else {
      if (vblForecastData.turbinesToForecast.length === 0) {
        errors.push('No turbines need forecasting');
      }
      if (Object.keys(vblForecastData.workspaceParameters).length === 0) {
        errors.push('No VBL analysis workspaces with valid parameters found');
      }
    }

    return errors;
  }, [vblForecastData]);

  // Summary statistics - only count turbines with valid component mappings
  const summaryStats = useMemo(() => {
    if (!vblForecastData) return null;

    const totalTurbines = vblForecastData.turbinesToForecast.length;

    // Count turbines with valid component mappings
    const mappedTurbines = vblForecastData.turbinesToForecast.filter(turbine => {
      const mapping = findBestComponentMapping(turbine, vblForecastData.mappings);
      return mapping && vblForecastData.workspaceParameters[mapping.workspace_id];
    }).length;

    const workspacesWithParameters = Object.keys(vblForecastData.workspaceParameters).length;

    return {
      totalTurbines,
      mappedTurbines,
      workspacesWithParameters
    };
  }, [vblForecastData]);

  // Auto-calculate when enabled and data is available
  useEffect(() => {
    if (autoCalculate && canCalculate && forecastResults.length === 0) {
      calculateForecast();
    }
  }, [autoCalculate, canCalculate, forecastResults.length, calculateForecast]);

  return {
    // Data
    turbinesToForecast: vblForecastData?.turbinesToForecast || [],
    workspaceMappings: vblForecastData?.mappings || [],
    workspaceParameters: vblForecastData?.workspaceParameters || {},
    forecastResults,

    // Loading states
    isLoadingData,
    isCalculating,
    calculationProgress,
    progressText,

    // Actions
    calculateForecast,
    clearResults,
    refetchData,

    // Validation
    canCalculate,
    validationErrors,
    summaryStats,

    // Errors
    dataError
  };
}